import { environment } from "@env/environment";
import { AuthService } from "@services/auth.service";
import { SessionStore } from "@state/session/session.store";
import { catchError, from, switchMap, throwError } from "rxjs";

import {
  HttpErrorResponse,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";

export const authenticationInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const sessionStore = inject(SessionStore);
  const token = sessionStore.ssoToken();

  if (!token || req.url.includes("/sso/auth/")) {
    return next(req);
  }

  const apiRoot = `${environment.API_URL.replace(/\/$/, "")}/api/`;
  const canRecoverSession =
    req.url.startsWith(apiRoot) &&
    !req.url.includes("/api/governance/") &&
    !req.url.includes("/public/");
  const authService = canRecoverSession ? inject(AuthService) : null;
  const sessionGeneration = authService?.getSessionGeneration();
  const modifiedReq = req.clone({
    headers: req.headers.set("Authorization", `Bearer ${token}`),
  });

  return next(modifiedReq).pipe(
    catchError(error => {
      if (
        !authService ||
        sessionGeneration === undefined ||
        !(error instanceof HttpErrorResponse) ||
        error.status !== 401
      ) {
        return throwError(() => error);
      }

      return from(
        authService.recoverSessionAfterUnauthorized(token, sessionGeneration)
      ).pipe(
        switchMap(recovered => {
          if (
            !recovered ||
            !authService.isSessionGenerationCurrent(sessionGeneration)
          ) {
            return throwError(() => error);
          }

          const refreshedToken = sessionStore.ssoToken();
          if (!refreshedToken) return throwError(() => error);

          return next(
            modifiedReq.clone({
              headers: modifiedReq.headers.set(
                "Authorization",
                `Bearer ${refreshedToken}`
              ),
            })
          );
        })
      );
    })
  );
};
